/**
 * The edits that turn a document into its formatted text, as small separate
 * hunks (#987): a format touches spacing all over a file, and writing it as one
 * block from the first change to the last would delete and re-insert almost the
 * whole file — every collaborator's caret would jump, and typing inside that
 * span would collide with it. Lines are diffed first, then each changed block is
 * trimmed. With `chars`, a small block is split further by characters: two
 * changes on one line stay two hunks, so a caret (or a keystroke) between them
 * stays put — for syncing an editor and rebasing an edit. Never for a FORMAT:
 * paired tokens (quotes, brackets) become separate hunks, and dropping one that
 * overlaps a concurrent edit while keeping its partner would write code that
 * doesn't parse; a format's hunk is a whole changed block.
 */
import { diffArrays, diffChars } from 'diff'

/** A changed block up to this size (each side, after trimming) is split by characters… */
const CHAR_SPLIT_MAX = 2000
/** …within this many character edits (diffChars is O(N·D)); past it, one span — the alignment would be noise anyway. */
const CHAR_SPLIT_EDITS = 64

/** Replace `before[from, to)` with `insert`. Hunks are in document order and never overlap. */
export interface TextHunk {
  from: number
  to: number
  insert: string
}

function splitLines(text: string): string[] {
  const lines = text.split(/(?<=\n)/)
  return lines.length === 1 && lines[0] === '' ? [] : lines
}

function blockHunks(removed: string, added: string, at: number, chars: boolean): TextHunk[] {
  let start = 0
  const max = Math.min(removed.length, added.length)
  while (start < max && removed.charCodeAt(start) === added.charCodeAt(start)) start++
  let endR = removed.length
  let endA = added.length
  while (endR > start && endA > start && removed.charCodeAt(endR - 1) === added.charCodeAt(endA - 1)) { endR--; endA-- }
  if (start === endR && start === endA) return []
  const r = removed.slice(start, endR)
  const a = added.slice(start, endA)
  const whole = [{ from: at + start, to: at + endR, insert: a }]
  if (!chars || !r || !a || r.length > CHAR_SPLIT_MAX || a.length > CHAR_SPLIT_MAX) return whole
  const parts = diffChars(r, a, { maxEditLength: CHAR_SPLIT_EDITS })
  if (!parts) return whole
  const out: TextHunk[] = []
  let pos = at + start
  let cur: TextHunk | null = null
  for (const part of parts) {
    if (part.added) {
      cur ??= { from: pos, to: pos, insert: '' }
      cur.insert += part.value
    } else if (part.removed) {
      cur ??= { from: pos, to: pos, insert: '' }
      pos += part.value.length
      cur.to = pos
    } else {
      if (cur) { out.push(cur); cur = null }
      pos += part.value.length
    }
  }
  if (cur) out.push(cur)
  return out
}

/** With `maxEditLength`, null when the texts differ by more lines than that (the diff gives up). `chars`: see above. */
export function formatHunks(before: string, after: string): TextHunk[]
export function formatHunks(before: string, after: string, opts: { maxEditLength: number; chars?: boolean }): TextHunk[] | null
export function formatHunks(before: string, after: string, opts?: { maxEditLength: number; chars?: boolean }): TextHunk[] | null {
  if (before === after) return []
  const chars = opts?.chars === true
  const parts = opts ? diffArrays(splitLines(before), splitLines(after), { maxEditLength: opts.maxEditLength }) : diffArrays(splitLines(before), splitLines(after))
  if (!parts) return null
  const hunks: TextHunk[] = []
  let pos = 0
  let removed = ''
  let added = ''
  let blockStart = 0
  const flush = () => {
    if (removed || added) {
      hunks.push(...blockHunks(removed, added, blockStart, chars))
    }
    removed = ''
    added = ''
  }
  for (const part of parts) {
    const text = part.value.join('')
    if (part.added) {
      if (!removed && !added) blockStart = pos
      added += text
    } else if (part.removed) {
      if (!removed && !added) blockStart = pos
      removed += text
      pos += text.length
    } else {
      flush()
      pos += text.length
    }
  }
  flush()
  return hunks
}

/** `before` with `hunks` applied — what the hunks are checked against. */
export function applyHunks(before: string, hunks: readonly TextHunk[]): string {
  let out = ''
  let pos = 0
  for (const h of hunks) {
    out += before.slice(pos, h.from) + h.insert
    pos = h.to
  }
  return out + before.slice(pos)
}
